import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Route } from '../types/route';
import { RouteMonthlyCheckin } from '../types/boarding';
import { aggregateStationPassengers, normalizeRoute, PassengerStation } from '../utils/stationPassengers';

interface MonthData {
  routes: Route[];
  stations: PassengerStation[];
  documents: RouteMonthlyCheckin[];
}

// Session-only cache, shared by list/detail. No polling or realtime checkin subscription.
const months = new Map<string, Promise<MonthData>>();
let cacheOwner = '';
const EMPTY: MonthData = { routes: [], stations: [], documents: [] };

export function useStationPassengers(selectedDate: string, test: boolean, routeFilter: string | null) {
  const { user, adminType } = useAuth();
  const uid = user?.uid;
  const yearMonth = selectedDate.slice(0, 7).replace('-', '');
  const owner = `${uid || ''}:${adminType || ''}`;
  const key = `${owner}:${yearMonth}:${test}`;
  const [state, setState] = useState<{ key: string; data: MonthData; error: string }>({ key: '', data: EMPTY, error: '' });

  useEffect(() => {
    let active = true;
    if (!uid || !/^\d{6}$/.test(yearMonth)) return;
    if (cacheOwner !== owner) { months.clear(); cacheOwner = owner; }
    let pending = months.get(key);
    if (!pending) {
      pending = Promise.all([
        getDocs(collection(db, test ? 'test_routes' : 'routes')),
        getDocs(collection(db, test ? 'test_station' : 'stations')),
        getDocs(query(collection(db, 'route_monthly_checkins'), where('year_month', '==', yearMonth))),
      ]).then(([routes, stations, documents]) => ({
        routes: routes.docs.map(doc => normalizeRoute(doc.id, doc.data())),
        stations: stations.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id, aliases: [doc.id, String(data.id ?? doc.id)],
            name: String(data.name || data.stationName || data.station_name || doc.id).trim(),
            order: typeof data.order === 'number' ? data.order : Number.MAX_SAFE_INTEGER,
          };
        }),
        documents: documents.docs.map(doc => ({ ...doc.data(), id: doc.id } as RouteMonthlyCheckin)),
      }));
      // Bound retained raw passenger data when browsing many months.
      if (months.size >= 3) months.delete(months.keys().next().value!);
      months.set(key, pending);
    }
    pending.then(data => { if (active) setState({ key, data, error: '' }); }).catch(error => {
      months.delete(key);
      console.error('Error loading monthly station checkins:', error);
      if (active) setState({ key, data: EMPTY, error: '월간 체크인·노선·정류장 데이터를 불러오지 못했습니다. 페이지를 새로고침해 주세요.' });
    });
    return () => { active = false; };
  }, [key, owner, uid, yearMonth, test]);

  const data = state.key === key ? state.data : EMPTY;
  const routes = useMemo(() => data.routes.filter(route => !routeFilter || route.name.includes(routeFilter)), [data.routes, routeFilter]);
  const aggregation = useMemo(() => aggregateStationPassengers(data.documents, routes, data.stations, yearMonth), [data, routes, yearMonth]);
  const entries = useMemo(() => Array.from(aggregation.counts, ([key, counts]) => {
    const [date, routeId, stationName] = JSON.parse(key) as [string, string, string];
    return { date, routeId, stationName, ...counts };
  }), [aggregation]);
  const unmatchedTotal = Array.from(aggregation.unmatched.values()).reduce((sum, count) => sum + count, 0);
  return { ...aggregation, unmatchedTotal, entries, routes, stations: data.stations, loading: state.key !== key,
    error: state.key === key ? state.error : '' };
}
